/**
 * ============================================================================
 * useAvailableShortcuts — Filtra o registro de atalhos pelo tenant/usuário
 * ----------------------------------------------------------------------------
 * RELEASE: 2026-10-18 v1.2.0 - Gate de Ordem de Serviço, Gestão Rastreador e Logística.
 * RELEASE: 2026-10-02 v1.1.0 - Gate dos módulos Veículos, Retenção, Jurídico e Workflow.
 * RELEASE: 2026-07-24 v1.0.0 - Criação. Cruza APP_SHORTCUTS com as flags de
 *   módulo (useHeaderModuleFlags) e as permissões do usuário (usePermissions),
 *   retornando apenas o que o usuário pode acessar.
 * ============================================================================
 */
import { useMemo } from 'react';
import { APP_SHORTCUTS, type AppShortcutDef, type ShortcutModule } from '@/config/appShortcuts';
import { useHeaderModuleFlags } from '@/hooks/useHeaderModuleFlags';
import { usePermissions } from '@/hooks/usePermissions';
import { useServiceOrderModuleEnabled } from '@/hooks/useServiceOrderModule';
import { useStockModuleEnabled } from '@/hooks/useStockModule';

export function useAvailableShortcuts(): AppShortcutDef[] {
  const {
    isCrmAvailable,
    isFinancialAvailable,
    isTasksAvailable,
    isReferralAvailable,
    isActivitiesAvailable,
    isKnowledgeAvailable,
    isVehiclesAvailable,
    isRetentionAvailable,
    isLegalAvailable,
    isWorkflowAvailable,
  } = useHeaderModuleFlags();
  const { canView, isAdmin, loading } = usePermissions();
  // v1.2.0: flags próprias dos módulos (mesmo gate do menu lateral)
  const isServiceOrdersAvailable = useServiceOrderModuleEnabled();
  const isStockAvailable = useStockModuleEnabled();

  return useMemo(() => {
    if (loading) return [];

    const moduleEnabled = (m: ShortcutModule): boolean => {
      switch (m) {
        case 'conversations': return true;
        case 'shared':        return true;
        case 'crm':           return isCrmAvailable;
        case 'financial':     return isFinancialAvailable;
        case 'tasks':         return isTasksAvailable;
        case 'referral':      return isReferralAvailable;
        case 'activities':    return isActivitiesAvailable;
        case 'knowledge':     return isKnowledgeAvailable;
        // v1.1.0: módulos novos (Veículos, Retenção, Jurídico, Workflow)
        case 'vehicles':      return isVehiclesAvailable;
        case 'retention':     return isRetentionAvailable;
        case 'legal':         return isLegalAvailable;
        case 'workflow':      return isWorkflowAvailable;
        // v1.2.0: Logística é transversal (só a permissão log_shipments controla)
        case 'serviceOrders': return isServiceOrdersAvailable;
        case 'stock':         return isStockAvailable;
        case 'logistics':     return true;
        default:              return false;
      }
    };

    return APP_SHORTCUTS.filter((s) => {
      // 1) Módulo do tenant precisa estar ligado.
      if (!moduleEnabled(s.module)) return false;
      // 2) v1.4.0 — Rotas admin-only não aparecem para não-admin.
      if (s.requireAdmin && !isAdmin) return false;
      // 3) Sem menuKeys → apenas o gate de módulo importa.
      if (!s.menuKeys || s.menuKeys.length === 0) return true;
      // 4) Admin passa direto.
      if (isAdmin) return true;
      // 5) Pelo menos uma menuKey precisa estar liberada.
      return s.menuKeys.some((k) => canView(k));
    });
  }, [
    loading,
    isAdmin,
    canView,
    isCrmAvailable,
    isFinancialAvailable,
    isTasksAvailable,
    isReferralAvailable,
    isActivitiesAvailable,
    isKnowledgeAvailable,
    isVehiclesAvailable,
    isRetentionAvailable,
    isLegalAvailable,
    isWorkflowAvailable,
    isServiceOrdersAvailable,
    isStockAvailable,
  ]);
}
